import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Mail, RefreshCw, Send, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const WeeklyDigestWidget = () => {
  const [stats, setStats] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [since, setSince] = useState(7);
  const [msg, setMsg] = useState("");

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/admin/weekly-digest/stats`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setStats(r.data || {});
    } catch (e) {
      console.error("[weekly-digest] stats fetch failed", e?.response?.status);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const [emailPreview, setEmailPreview] = useState(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);

  const runEmailPreview = async () => {
    setEmailPreviewLoading(true);
    setMsg("");
    try {
      const r = await axios.get(
        `${API}/admin/weekly-digest/preview-html?since_days=${since}`,
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setEmailPreview(r.data || {});
    } catch (e) {
      setMsg(`Errore anteprima: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setEmailPreviewLoading(false);
    }
  };

  const runPreview = async () => {
    setLoading(true);
    setMsg("");
    setPreview(null);
    try {
      const r = await axios.post(
        `${API}/admin/weekly-digest/preview?since_days=${since}`,
        {},
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setPreview(r.data || {});
    } catch (e) {
      setMsg(`Errore: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runSend = async (force = false) => {
    const confirmMsg = force
      ? "Forzare l'invio (ignora la dedup settimanale)?\n\nCONSIGLIATO solo per testing."
      : "Inviare ora la newsletter dei nuovi arrivi ai clienti affini?";
    if (!window.confirm(confirmMsg)) return;
    setSending(true);
    setMsg("");
    try {
      const r = await axios.post(
        `${API}/admin/weekly-digest/send?since_days=${since}&force=${force}`,
        {},
        { headers: getAuthHeaders(), withCredentials: true },
      );
      const d = r.data || {};
      setMsg(
        `✅ Inviate ${d.emails_sent}/${d.customers_evaluated} email · `
        + `${d.emails_skipped_no_match} senza match · `
        + `${d.emails_skipped_already_sent} già inviate questa settimana · `
        + `${d.emails_skipped_opt_out} opt-out · `
        + `${d.emails_failed} errori`,
      );
      fetchStats();
    } catch (e) {
      setMsg(`Errore: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="weekly-digest-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <Mail className="w-5 h-5 text-amber-400" />
          Newsletter Nuovi Arrivi (Settimanale)
        </CardTitle>
        <button
          type="button"
          onClick={fetchStats}
          className="text-gray-300 hover:text-white p-1"
          aria-label="Aggiorna"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-900/60 rounded-lg p-3">
              <p className="text-[11px] text-gray-400 uppercase">Email 30gg</p>
              <p className="text-2xl font-bold text-white mt-1" data-testid="wd-stat-30d">
                {stats.total_30d || 0}
              </p>
            </div>
            <div className="bg-gray-900/60 rounded-lg p-3">
              <p className="text-[11px] text-gray-400 uppercase">Disiscritti</p>
              <p className="text-2xl font-bold text-amber-400 mt-1" data-testid="wd-stat-optout">
                {stats.opted_out_total || 0}
              </p>
            </div>
            <div className="bg-gray-900/60 rounded-lg p-3">
              <p className="text-[11px] text-gray-400 uppercase">Settimane attive</p>
              <p className="text-2xl font-bold text-emerald-400 mt-1" data-testid="wd-stat-weeks">
                {(stats.by_week || []).length}
              </p>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[11px] text-gray-400 uppercase block mb-1">
              Articoli degli ultimi (giorni)
            </label>
            <input
              type="number"
              min="1"
              max="400"
              value={since}
              onChange={(e) => setSince(parseInt(e.target.value || "7", 10))}
              className="bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 w-24"
              data-testid="wd-since-days"
            />
          </div>
          <Button
            onClick={runPreview}
            disabled={loading}
            variant="outline"
            className="border-gray-600 text-gray-200 hover:bg-gray-700"
            data-testid="wd-preview-btn"
          >
            <Eye className="w-4 h-4 mr-1" /> {loading ? "..." : "Anteprima"}
          </Button>
          <Button
            onClick={runEmailPreview}
            disabled={emailPreviewLoading}
            variant="outline"
            className="border-pink-600 text-pink-300 hover:bg-pink-900/30"
            data-testid="wd-email-preview-btn"
          >
            <Mail className="w-4 h-4 mr-1" /> {emailPreviewLoading ? "..." : "Anteprima Email"}
          </Button>
          <Button
            onClick={() => runSend(false)}
            disabled={sending}
            className="bg-amber-500 text-black hover:bg-amber-400"
            data-testid="wd-send-btn"
          >
            <Send className="w-4 h-4 mr-1" /> {sending ? "Invio..." : "Invia adesso"}
          </Button>
          <Button
            onClick={() => runSend(true)}
            disabled={sending}
            variant="outline"
            className="border-red-600 text-red-300 hover:bg-red-900/30"
            data-testid="wd-send-force-btn"
            title="Ignora la dedup settimanale — solo per test"
          >
            Forza re-invio
          </Button>
        </div>

        {/* Help */}
        <p className="text-[11px] text-gray-500">
          Il job automatico parte ogni <strong>lunedì alle 09:00 (Europa/Roma)</strong>.
          Ogni cliente riceve max 1 email/settimana, solo se almeno un nuovo articolo
          combacia con la sua cronologia ordini (categoria/serie/brand).
        </p>

        {msg && (
          <div className="text-sm p-3 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-200" data-testid="wd-msg">
            {msg}
          </div>
        )}

        {/* Email HTML preview modal */}
        {emailPreview && (
          <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="wd-email-preview-modal" onClick={() => setEmailPreview(null)}>
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Oggetto email:</p>
                  <p className="text-sm font-semibold text-gray-900 truncate" data-testid="wd-email-preview-subject">{emailPreview.subject || "(nessun invio previsto)"}</p>
                </div>
                <button className="text-gray-500 hover:text-gray-800 text-xl px-2" onClick={() => setEmailPreview(null)} data-testid="wd-email-preview-close">✕</button>
              </div>
              <iframe
                title="Anteprima newsletter"
                srcDoc={emailPreview.html || ""}
                className="w-full flex-1 min-h-[60vh] bg-white"
                sandbox=""
                data-testid="wd-email-preview-iframe"
              />
              <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500">
                Anteprima con destinatario di esempio ("Mario") e i {emailPreview.new_articles || 0} nuovi articoli degli ultimi {since} giorni. Ogni cliente riceverà solo gli articoli affini ai suoi acquisti.
              </div>
            </div>
          </div>
        )}

        {/* Preview details */}
        {preview && (
          <div className="border-t border-gray-700 pt-3 space-y-2" data-testid="wd-preview-output">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
              <Mini label="Nuovi" value={preview.new_articles} color="text-white" />
              <Mini label="Clienti analizzati" value={preview.customers_evaluated} color="text-white" />
              <Mini label="Con match" value={(preview.details || []).length} color="text-emerald-400" />
              <Mini label="Senza match" value={preview.emails_skipped_no_match} color="text-gray-400" />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2 pt-2">
              {(preview.details || []).slice(0, 20).map((d, i) => (
                <div key={i} className="bg-gray-900/60 rounded p-2 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-amber-300 font-semibold">{d.email}</span>
                    <span className="text-gray-500">{d.name}</span>
                  </div>
                  <p className="text-gray-300 italic mb-1">Subject: {d.subject}</p>
                  <ul className="text-gray-400 space-y-0.5">
                    {(d.articles || []).map((a) => (
                      <li key={a.id}>• {a.title} <span className="text-amber-500">(score {a.score})</span></li>
                    ))}
                  </ul>
                </div>
              ))}
              {(preview.details || []).length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  Nessun cliente con match. Prova ad aumentare i giorni.
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Mini = ({ label, value, color }) => (
  <div className="bg-gray-900/60 rounded p-2">
    <p className="text-[10px] text-gray-500 uppercase">{label}</p>
    <p className={`text-xl font-bold mt-0.5 ${color}`}>{value ?? 0}</p>
  </div>
);

export default WeeklyDigestWidget;
